import React from "react";
import { Link } from "react-router-dom";
import { fileViewUrl } from "../lib/api";
import { Calendar, MapPin, Users, Megaphone } from "lucide-react";

/**
 * Auto-scrolling marquee gallery of the 5 most recent awareness programs.
 * Renders two rows that scroll in opposite directions for a dynamic feel.
 * Items duplicate to create an infinite illusion. Hover pauses the animation.
 */
export default function AwarenessGallery({ items = [] }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-xl border border-brand-border bg-white p-8 text-center" data-testid="awareness-gallery-empty">
        <Megaphone className="h-7 w-7 text-brand-muted mx-auto" />
        <div className="mt-3 font-heading font-semibold">No awareness programs yet</div>
        <div className="text-sm text-brand-muted mt-1">Programs added by students/faculty will showcase here.</div>
      </div>
    );
  }

  // Duplicate items so the marquee can loop seamlessly
  const looped = [...items, ...items];

  return (
    <div className="space-y-3" data-testid="awareness-gallery">
      <MarqueeRow items={looped} direction="left" testIdPrefix="aw-row1" />
      {items.length > 1 && (
        <MarqueeRow items={[...looped].reverse()} direction="right" testIdPrefix="aw-row2" />
      )}
    </div>
  );
}

function MarqueeRow({ items, direction, testIdPrefix }) {
  const cls = direction === "right" ? "marquee-track marquee-right" : "marquee-track marquee-left";
  return (
    <div className="marquee-row overflow-hidden rounded-xl" data-testid={`${testIdPrefix}`}>
      <div className={cls}>
        {items.map((a, i) => (
          <GalleryCard key={`${a.id}-${i}`} program={a} />
        ))}
      </div>
    </div>
  );
}

function GalleryCard({ program }) {
  const photo = program.photo_paths?.[0];
  return (
    <Link
      to={`/awareness/${program.id}`}
      className="group relative w-[280px] sm:w-[320px] shrink-0 overflow-hidden rounded-xl border border-brand-border bg-white shadow-sm hover:shadow-lg transition-all duration-300"
      data-testid={`aw-card-${program.id}`}
    >
      <div className="relative aspect-[16/10] bg-brand-surfaceAlt overflow-hidden">
        {photo ? (
          <img
            src={fileViewUrl(photo)}
            alt={program.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full grid place-items-center text-brand-muted">
            <Megaphone className="h-8 w-8 opacity-60" />
          </div>
        )}
        {/* Topic badge */}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-primary shadow-sm">
          {program.topic}
        </span>
        {/* Attendees pill */}
        {program.attendees > 0 && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-brand-primary text-white px-2.5 py-1 text-[10px] font-bold">
            <Users className="h-3 w-3" /> {program.attendees}
          </span>
        )}
        {/* Gradient overlay for legibility */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/65 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 text-white">
          <div className="font-heading text-base font-semibold line-clamp-1 drop-shadow">{program.title}</div>
          <div className="flex items-center gap-3 text-[11px] mt-1 opacity-95">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {program.village}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {program.program_date || "—"}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
